import { LINE_COLOR } from '../constants.js';
import { latencyPoints } from '../format.js';

interface Series {
  name: string;
  color: string;
  values: number[];
}

interface Props {
  eventLatency: number[];
  agoraLatency: number[];
}

/** Only show the section once both products have a full 30 days recorded. */
const FULL_DAYS = 30;

/** SVG line chart of 30-day daily response times. */
export function LatencyChart({ eventLatency, agoraLatency }: Props) {
  // Both series are self-recorded and fill day by day. Hide the whole section
  // until *both* products have a genuine 30-day window, so users never see a
  // half-drawn line or a single product standing in for the pair.
  if (eventLatency.length < FULL_DAYS || agoraLatency.length < FULL_DAYS) {
    return null;
  }

  const series: Series[] = [
    { name: 'ArtifaxEvent', color: LINE_COLOR.event, values: eventLatency },
    { name: 'ArtifaxAgora', color: LINE_COLOR.agora, values: agoraLatency },
  ];

  const all = series.flatMap((s) => s.values);
  const min = Math.min(...all);
  const max = Math.max(...all);
  const summary = series
    .map((s) => `${s.name} ${s.values[s.values.length - 1]} ms`)
    .join('; ');

  return (
    <section className="chart" aria-labelledby="latency-h">
      <div className="chart__head">
        <h2 className="chart__title" id="latency-h">
          Response time · last 30 days
        </h2>
        <div className="legend">
          {series.map((s) => (
            <div className="legend__item" key={s.name}>
              <span className="legend__swatch" style={{ background: s.color }} aria-hidden="true" />
              <span className="legend__name">{s.name}</span>
              <span className="legend__value">{s.values[s.values.length - 1]} ms</span>
            </div>
          ))}
        </div>
      </div>
      <svg
        viewBox="0 0 620 180"
        preserveAspectRatio="none"
        className="chart__svg"
        role="img"
        aria-label={`Daily average response time, last 30 days — current: ${summary}.`}
      >
        {[50, 95, 140].map((y) => (
          <line key={y} x1="12" y1={y} x2="608" y2={y} stroke="rgba(130,120,135,.16)" strokeWidth="1" />
        ))}
        {series.map((s) => (
          <polyline
            key={s.name}
            points={latencyPoints(s.values, min, max)}
            fill="none"
            stroke={s.color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      <div className="chart__axis">
        <span>30 days ago</span>
        <span>now</span>
      </div>
    </section>
  );
}
